import { useState } from "react";
import parse from "html-react-parser";
import {
  GoogleMap,
  Marker,
  OverlayView,
  useLoadScript,
} from "@react-google-maps/api";
import AgencyCard from "@/components/airport/map/AgencyCard";
import React from "react";
import { getUniqueAgencies } from "@/utils/uniqueParse";

type Props = {
  title?: string;
  subtitle?: string;
  locationName: string;
  airportNameData: AirportNameData;
  airportData: any;
  cardata: CarData[];
  locale: string;
};

const CustomMarker = ({ url }: { url: string }) => {
  return (
    <div className="px-4 py-2 bg-white rounded-tl-none shadow-2xl rounded-xl border-[#E5EAF6] border">
      <img loading="lazy" src={url} className="lg:h-[24px] h-[20px]" alt={`agency map logo`} />
    </div>
  );
};

const ClassMap = (props: Props) => {
  const [map, setMap] = useState<any>(null);

  const agency = props.cardata[0].agency;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
    libraries: ["places"],
  });
  const containerStyle = {
    width: "100%",
    height: "400px",
    borderRadius: "10px",
  };

  const center = {
    lat: props.airportData.lat,
    lng: props.airportData.lng,
  };
  const dynamicContent = ``;
  return (
    <div className="mt-5 lg:mt-[60px] bg-white px-[13px] py-[30px]">
      <div className="mx-auto max-w-7xl">
        <div className="flex justify-between w-full">
          <div className="space-y-[6px] lg:space-y-[10px] mb-[18px]">
            <h2 className="text-base font-extrabold lg:text-3xl text-my-dark-blue">
              {props.title
                ? props.title
                : `Car Rental Directions from ${props.locationName}`}
            </h2>
            <div className="space-y-6 text-sm font-normal text-my-light-blue lg:font-light lg:text-base">
              {parse(props.subtitle ? props.subtitle : ``)}
            </div>
          </div>
        </div>

        <div>
          {props.cardata && isLoaded ? (
            <GoogleMap
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                styles: mapStyles,
              }}
              mapContainerStyle={containerStyle}
              center={center}
              zoom={10}
              onLoad={(map) => {
                setMap(map);
                const bounds = new window.google.maps.LatLngBounds();
                bounds.extend(
                  new google.maps.LatLng(
                    agency.pickupLocation.lat,
                    agency.pickupLocation.lon
                  )
                );
                bounds.extend(new google.maps.LatLng(center.lat, center.lng));
                bounds.extend(
                  new google.maps.LatLng(
                    props.airportData.lat,
                    props.airportData.lon
                  )
                );
                map.fitBounds(bounds);
              }}
              onUnmount={() => setMap(null)}
            >
              {props.cardata ? (
                props.cardata.map((car: CarData) => (
                  <OverlayView
                    key={car.id}
                    position={{
                      lat: car.agency.pickupLocation.lat,
                      lng: car.agency.pickupLocation.lon,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <CustomMarker
                      url={
                        car.agency.name === "Surprise Agency"
                          ? "/rental-company-list/surprise.svg"
                          : `https://content.r9cdn.net/rimg/provider-logos/cars/h/${car.agency.icon}`
                      }
                    />
                  </OverlayView>
                ))
              ) : (
                <></>
              )}
              {props.airportData && (
                <Marker
                  position={{
                    lat: props.airportData.lat,
                    lng: props.airportData.lng,
                  }}
                  title={props.airportData.code}
                  icon={{
                    url: "/airport-pin.svg",
                  }}
                />
              )}
            </GoogleMap>
          ) : (
            <></>
          )}
        </div>
        <div className="grid grid-cols-1 py-4 lg:grid-cols-2 lg:gap-2">
          {props.cardata &&
            getUniqueAgencies(props.cardata).map((agency) => (
              <AgencyCard
                key={agency.agency.providerCode}
                agency={agency.agency}
                provider={agency.provider}
                airportData={props.airportNameData}
                airportCoords={{
                  lat: props.airportData.lat,
                  lon: props.airportData.lng,
                }}
                locale={props.locale}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ClassMap);

const mapStyles = [
  {
    featureType: "administrative.land_parcel",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "administrative.neighborhood",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "landscape.man_made",
    stylers: [
      {
        visibility: "simplified",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi.business",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.arterial",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.local",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "transit",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "transit.station.airport",
    stylers: [
      {
        visibility: "on",
      },
    ],
  },
  {
    featureType: "transit.station.airport",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
];
